/**
 * `DetailSurfaceHero` — hero image render seam shared by every
 * article-detail surface.
 *
 * Hero image — single render seam
 * ================================
 *
 * The hero `<img>` block is owned here, not duplicated across each
 * populated branch of every article-detail route. Every consuming
 * route calls `<DetailSurfaceHero imageUrl={article.imageUrl}
 * mediaLabel={article.title} />` immediately before its `<h1>` (the
 * `mediaLabel` names the `<video>` branch only — see below); the helper
 * renders the proto-aligned
 * 240px-tall `object-cover` block when `imageUrl` is non-null and
 * renders nothing (returns `null`) when it is.
 *
 * NO SEEDED ARTICLE reaches that null branch today: all five surviving
 * articles carry a hero. The three that did not — oikeusturvavakuutus,
 * premium and tama-saat-jasenena — were deleted by the owner ruling of
 * 2026-08-28. The branch keeps its code because an author can clear a
 * hero through the CMS; it simply has no producer in the seed.
 *
 * (This comment previously sourced those null `imageUrl` values to
 * "migration 0010". There is no 0010 — the chain has been three files
 * since the collapse — so that clause was already false before the
 * articles were deleted.)
 *
 * The `<img>` hero is decorative `alt=""` by default, informative
 * (`alt={heroImageAlt}`) when an author supplies alt text — see the
 * JSDoc on `DetailSurfaceHero` below.
 *
 * CSP TODO (deferred until D-004 / CMS handoff)
 * =============================================
 *
 * The Phase A asset URL pattern is `/media/<opaque-uuid>.png` —
 * same-origin static, served by Vite (dev) or by the edge (prod).
 * `img-src 'self'` is sufficient. When a CMS adapter lands and starts
 * writing external-origin URLs into `content_items.image_url`, the
 * edge `Content-Security-Policy` header will need its `img-src`
 * directive widened to allow the CMS-vendor's CDN host(s). The widen
 * is deferred until D-004 resolves with a chosen vendor.
 *
 * Why a separate module
 * =====================
 *
 * Lifted out of `articleDetailSurface.tsx` to keep that module under
 * the FR-045 file-size ceiling (400 lines). The hero is a single
 * render seam with its own render-time concerns (CLS hardening,
 * empty-alt rationale, dev-mode load-error breadcrumb); a colocated
 * test file (`__tests__/detailSurfaceHero.test.tsx`) pins the shape
 * contract. `articleDetailSurface.tsx` retains the layout token
 * constants, `DetailSurfaceSkeleton`, `DetailSurfaceNotFound`, and
 * the `PolicyStatusCard` re-export.
 */

import type { JSX } from 'react';

import { isVideoUrl } from '../../components/cms/isVideoUrl.js';
import { useTranslate } from '../../i18n/useTranslate.js';

// Hero render tokens — match the proto's article hero block exactly:
//   - `mt-4 mb-8` outer rhythm so the hero sits between the page padding
//     and the H1 with a comfortable gap.
//   - `w-full h-hero-image` fixed-height container (240px / 15rem,
//     defined as `--spacing-hero-image` in `tokens.css`); `object-cover`
//     crops the underlying PNG to fill without letterboxing.
//   - `rounded-lg` matches the proto's image treatment.
const heroWrapperClasses: string = 'w-full mb-8 mt-4';
const heroImageClasses: string = 'w-full h-hero-image object-cover rounded-lg';

export interface DetailSurfaceHeroProps {
  /**
   * The article's hero image URL, or `null` when the article ships
   * without a hero. The component renders the proto-aligned `<img>`
   * block when non-null and returns `null` when null — consumers
   * therefore call it unconditionally and let the helper own the
   * presence decision (no `imageUrl !== null` checks at the call
   * site).
   */
  readonly imageUrl: string | null;
  /**
   * The article / page title, used ONLY as the `aria-label` for the
   * `<video>` hero branch (a focusable, interactive control needs an
   * accessible name; the `<img>` branch is decorative `alt=""` by default,
   * the H1 names the page). Passed by every call site, which already
   * has the title in scope. Optional so a caller that has no title (none
   * today) degrades gracefully — when omitted, the `<video>` falls back
   * to a generic `content.hero.videoLabel` name so it is never a focusable
   * control without an accessible name (rather than a type error).
   */
  readonly mediaLabel?: string;
  /**
   * OPTIONAL author-supplied alternative text for the `<img>` hero
   * (024-cms page-step parity, 10d — the article payload's
   * `heroImageAlt`). Mirrors `imageUrl`'s `string | null` shape so the
   * `Article.imageAlt` field threads through verbatim (no `?? undefined`
   * bridge at the call site). Absent / `null` / blank → the hero stays
   * decorative `alt=""` (the prior, backward-compatible default; the H1
   * names the page). A non-blank value makes the hero INFORMATIVE
   * (`alt={heroImageAlt}`). Applies to the `<img>` branch only — the
   * `<video>` branch names itself via `mediaLabel`.
   */
  readonly imageAlt?: string | null;
}

/**
 * Per-article hero image block. Owned here as the single render seam
 * so all article-detail surfaces (the generic `ArticleDetailPage` and
 * the bespoke routes) share the identical markup, classes, and a11y
 * posture.
 *
 * Markup. The wrapping `<div>` carries the outer rhythm (`mt-4 mb-8`);
 * the `<img>` inside is `h-hero-image object-cover rounded-lg` — a
 * fixed-height card (240px / 15rem, via the token-backed
 * `--spacing-hero-image` utility) that crops the PNG to fill without
 * letterboxing. Matches the proto's `TyosuhteenPaattaminen.tsx` hero
 * block in shape with one divergence: the proto's `h-[240px]` bracket
 * class is replaced by the token-backed `h-hero-image` utility so the
 * no-token-bypass arch check (FR-047) stays green. Cloners MUST cite
 * `h-hero-image`, not `h-[240px]`.
 *
 * CLS + paint-perf. `width="1200"` / `height="240"` encode the 5:1
 * aspect ratio so the browser reserves the box before CSS binds
 * (layout-reservation hints — CSS still wins at runtime).
 * `decoding="async"` decodes off the main thread without affecting
 * LCP candidacy. `loading` is intentionally NOT set: the hero is the
 * above-fold LCP candidate, so eager-load (the default) is correct —
 * `loading="lazy"` would deprioritise the most important paint.
 *
 * `alt` — decorative by default, informative when authored. With no
 * `imageAlt`, the hero renders `alt=""`: the H1 immediately following it
 * carries the page's accessible name, and a blanket `alt={title}` (the
 * proto's shape) would either duplicate it (screen-reader
 * double-announcement) or diverge from it (misleading the AT user) — so
 * the default treats the image as decorative. When an author supplies
 * `imageAlt` (the article `heroImageAlt`, 10d), the hero becomes
 * informative and renders `alt={imageAlt}`: the author has taken explicit
 * responsibility for a meaningful description, so we honour it. A
 * whitespace-only `imageAlt` is treated as absent (decorative `''`).
 *
 * Dev-mode load-error breadcrumb. `onError` warns in dev when the
 * asset fails to load (404, CSP block, …) so URL→file drift surfaces
 * fast during authoring; no-op in production. Complements the
 * `media-asset-integrity` arch test (build-time URL→file check on the
 * migration set) for external-origin URLs the future CMS will write.
 *
 * @param props - {@link DetailSurfaceHeroProps}
 */
export function DetailSurfaceHero({
  imageUrl,
  mediaLabel,
  imageAlt,
}: DetailSurfaceHeroProps): JSX.Element | null {
  const t = useTranslate();
  if (imageUrl === null) {
    return null;
  }
  // 024-cms Phase U: an image field may hold a video `/media/...mp4` URL. Render
  // `<video controls>` for a video URL, the existing `<img>` otherwise — same
  // fixed-height `object-cover` box either way. The dev-mode URL→file drift
  // breadcrumb (`onError`) is preserved on both element types.
  //
  // The hero is PRIMARY content, so its video is intentionally INTERACTIVE:
  // `controls`, and deliberately NOT `muted` (it should play with sound). Do
  // NOT add `muted` "for consistency" with the decorative poster sites
  // (HomeCards / benefit logo / thumbnail) — those are decorative, this is not.
  // The focusable `<video>` carries the page title as its `aria-label` so AT
  // users get a name (the `<img>` branch is decorative `alt=""` by default,
  // informative when an author supplies `imageAlt`). `mediaLabel` is optional,
  // so it falls back to a generic localized "Video" name — the control is never
  // exposed without an accessible name.
  const onError = (): void => {
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: dev-only observability for hero asset URL→file drift
      console.warn(`hero media failed to load: ${imageUrl}`);
    }
  };
  return (
    <div className={heroWrapperClasses}>
      {isVideoUrl(imageUrl) ? (
        // biome-ignore lint/a11y/useMediaCaption: author-uploaded hero media carries no caption track; a member-supplied .vtt is out of scope for Phase U
        <video
          src={imageUrl}
          className={heroImageClasses}
          width="1200"
          height="240"
          controls
          aria-label={mediaLabel ?? t('content.hero.videoLabel')}
          onError={onError}
        />
      ) : (
        <img
          src={imageUrl}
          alt={imageAlt?.trim() ? imageAlt : ''}
          className={heroImageClasses}
          width="1200"
          height="240"
          decoding="async"
          onError={onError}
        />
      )}
    </div>
  );
}
